import React, { memo, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Notification } from '@xsolla/ui-core';
import { Modal } from '@xsolla/ui-modal';

import {
  getConnectedLaunchers,
  getLoginConfigurations,
  LoginProjectConfiguration,
  patchFineConfigurations
} from '../../../api';
import { useConfig } from '../../../contexts/config';
import { usePaths } from '../../../routes/routesHelper';
import { linkTo, useFirstRender } from '../../../utils';
import { Separator } from '../../ui';
import { LinksWrapper, Title, Wrapper } from './style';
import UrlList from './urlList';

type Props = {
  projectId: string;
  onClose: () => void;
};

const ConnectionsModal = ({ projectId, onClose }: Props): JSX.Element => {
  const { launcherPath } = usePaths();
  const intl = useIntl();
  const firstRender = useFirstRender();
  const { merchantId } = useConfig();
  const [projectConfig, setProjectConfig] = useState<LoginProjectConfiguration | null>(null);
  const [gamePages, setGamePages] = useState<Array<string>>([]);
  const [connectedLaunchers, setConnectedLaunchers] = useState<Array<string>>([]);
  const [fetchingProject, setFetchingProject] = useState<boolean>(true);
  const [puttingProject, setPuttingProject] = useState<boolean>(false);
  const [fetchingLaunchers, setFetchingLaunchers] = useState<boolean>(true);

  useEffect(() => {
    getConnectedLaunchers({ merchantId, loginProjectId: projectId }).then((res) => {
      const launchersNames = res.map((launcher) => launcher.name);
      setConnectedLaunchers(launchersNames);
      setFetchingLaunchers(false);
    });

    getLoginConfigurations(projectId).then((res) => {
      setProjectConfig(res);
      if (res.game_pages) {
        setGamePages(res.game_pages);
      }
      setFetchingProject(false);
    });
  }, [projectId]);

  useEffect(() => {
    if (projectConfig && !firstRender && gamePages !== projectConfig.game_pages) {
      setPuttingProject(true);

      const data = {
        ...projectConfig,
        game_pages: gamePages
      };

      patchFineConfigurations(data, projectId).then(() => setPuttingProject(false));
    }
  }, [gamePages]);

  const addGamePage = (value: string) => setGamePages([...gamePages, value]);

  const removeGamePage = (id: number) => {
    gamePages.splice(id, 1);
    setGamePages([...gamePages]);
  };

  return (
    <Modal onClose={onClose}>
      <Wrapper>
        <Title>{intl.formatMessage({ id: 'dashboard.connections.label' })}</Title>

        <UrlList
          values={gamePages}
          onSaveClick={addGamePage}
          onRemoveClick={removeGamePage}
          label={intl.formatMessage({ id: 'dashboard.connections.website' })}
          allowCollapse
          fetching={fetchingProject}
          updatingList={puttingProject}
        />

        <Separator margin="32px 0 0" />

        <LinksWrapper>
          <UrlList
            values={connectedLaunchers}
            onSaveClick={addGamePage}
            onRemoveClick={removeGamePage}
            label={intl.formatMessage({ id: 'dashboard.connections.launcher' })}
            fetching={fetchingLaunchers}
            allowCollapse
            displayOnly
          />

          {!fetchingLaunchers && connectedLaunchers.length === 0 && (
            <Notification>
              <FormattedMessage
                id="dashboard.connections.info"
                values={linkTo({ href: launcherPath() })}
              />
            </Notification>
          )}
        </LinksWrapper>
      </Wrapper>
    </Modal>
  );
};

export default memo(ConnectionsModal);
